import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import {
  BenefitDocumentType,
  EmployerDto,
  EnrollmentStatus,
  OnboardingType,
  UpdateOnboardingPeriodByAdminDto,
} from '@zorro/clients';
import { UNEXPECTED_ERROR_MESSAGE } from '@zorro/common-utils';
import { formatDateISO, parseDateISO } from '@zorro/shared/formatters';
import {
  showErrorNotification,
  useBatchCallEndpoint,
  useForm,
  useIdempotencyKey,
  useLoadingOverlay,
  useMonolithMutation,
  useMonolithQuery,
} from '@zorro/web/ui-utils';

import {
  QleFormFields,
  useCreateQleBenefitDocuments,
  useRemoveQleBenefitDocuments,
} from '../../QualifyingLifeEvent';
import { RecordForActions } from '../EmployeeActionsUtils';
import { ErrorsTable } from '../Errors/ErrorsTable';
import { ChangeEnrollmentForm } from './ChangeEnrollmentForm';
import {
  ChangeEnrollmentsFormFields,
  FullChangeEnrollmentSchema,
} from './changeEnrollment.utils';

export interface EditEnrollmentsDrawerProps {
  employerId: EmployerDto['id'];
  recordsForActions: RecordForActions[];
  onSuccess: () => void;
}

export const EditEnrollmentsRecord = ({
  employerId,
  recordsForActions,
  onSuccess,
}: EditEnrollmentsDrawerProps) => {
  'use no memo';
  const idempotencyKey = useIdempotencyKey();
  const { startLoading, stopLoading } = useLoadingOverlay();
  const queryClient = useQueryClient();

  const isSingleEmployee = recordsForActions.length === 1;

  const { data: allOpenEnrollmentPeriods = [] } = useMonolithQuery({
    method: 'openEnrollmentPeriodsControllerFindAllForEmployer',
    params: [employerId],
  });

  const { data: employer } = useMonolithQuery({
    method: 'employersControllerFindOne',
    params: [employerId],
  });

  const { data: associatedOnboardingPeriodsSingleEmployee } = useMonolithQuery({
    method: 'onboardingPeriodsControllerFindManyWithEnrollmentStatus',
    params: isSingleEmployee && [recordsForActions[0]?.employeeId],
  });

  const associatedOtherOnboardingPeriodsSingleEmployee =
    associatedOnboardingPeriodsSingleEmployee?.filter(
      (onboardingPeriod) =>
        onboardingPeriod.id !== recordsForActions[0]?.onboardingPeriodId
    );

  const { data: singleEmployeeDto } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: isSingleEmployee && [recordsForActions[0]?.employeeId],
  });

  const { data: employerAgencies } = useMonolithQuery({
    method: 'agenciesControllerFindMany',
    params: [employerId],
  });

  const {
    executeBatchCall: updateEnrollments,
    errors: updateEnrollmentsErrors,
  } = useBatchCallEndpoint({
    singularItemName: 'enrollment record',
    action: 'updated',
    methodName: 'onboardingPeriodsControllerUpdateByAdmin',
  });

  const singleEmployeeOnboardingPeriod =
    associatedOnboardingPeriodsSingleEmployee
      ? associatedOnboardingPeriodsSingleEmployee.find(
          (period) => period.id === recordsForActions[0].onboardingPeriodId
        )
      : undefined;

  const previousQleDocuments =
    singleEmployeeOnboardingPeriod?.benefitDocuments?.filter(
      (document) => document.type === BenefitDocumentType.QLE
    );
  const previousIsQleEnrollment = Boolean(
    singleEmployeeOnboardingPeriod?.qualifyingLifeEvent
  );
  const previousQle: QleFormFields | undefined =
    singleEmployeeOnboardingPeriod?.qualifyingLifeEvent
      ? {
          type: singleEmployeeOnboardingPeriod.qualifyingLifeEvent.type,
          occurredOn: parseDateISO(
            singleEmployeeOnboardingPeriod.qualifyingLifeEvent.occurredOn
          ).toDate(),
          otherReason:
            singleEmployeeOnboardingPeriod.qualifyingLifeEvent.otherReason ??
            undefined,
          evidence: previousQleDocuments,
        }
      : undefined;

  const editModeSingleEmployeeDefaultValues:
    ChangeEnrollmentsFormFields | undefined = singleEmployeeOnboardingPeriod
    ? {
        planYear: singleEmployeeOnboardingPeriod.enrollmentPeriodId,
        enrollmentType: singleEmployeeOnboardingPeriod.isSpecialEnrollment
          ? OnboardingType.SPECIAL
          : OnboardingType.OPEN_ENROLLMENT,
        isQleEnrollment: previousIsQleEnrollment,
        qle: previousQle,
        electionWindow: [
          parseDateISO(singleEmployeeOnboardingPeriod.onboardingFrom).toDate(),
          parseDateISO(singleEmployeeOnboardingPeriod.onboardingUntil).toDate(),
        ],
        expectedStartOfCoverage: parseDateISO(
          singleEmployeeOnboardingPeriod.effectiveFrom
        ).toDate(),
      }
    : undefined;

  const form = useForm<ChangeEnrollmentsFormFields>({
    mode: 'all',
    resolver: yupResolver(FullChangeEnrollmentSchema),
    defaultValues: isSingleEmployee
      ? editModeSingleEmployeeDefaultValues
      : undefined, // no default values for bulk edit mode
  });

  const { tryMutate: updateOnboardingPeriod } = useMonolithMutation({
    method: 'onboardingPeriodsControllerUpdateByAdmin',
    successMessage: 'Enrollment record updated successfully!',
    shouldInvalidateQueries: false,
    shouldShowLoadingOverlay: false,
  });

  const { watch } = form;

  const planYear = watch('planYear');

  const selectedOEPId =
    planYear ?? singleEmployeeOnboardingPeriod?.enrollmentPeriodId;

  const enrollmentStatus = associatedOnboardingPeriodsSingleEmployee?.find(
    (period) => period.enrollmentPeriodId === selectedOEPId
  )?.enrollmentStatus;

  const hasLockedEnrollmentFields =
    Boolean(enrollmentStatus) &&
    !(
      enrollmentStatus &&
      [
        EnrollmentStatus.PENDING_ELECTION_WINDOW,
        EnrollmentStatus.ELECTION_ACTIVE_HAS_NOT_STARTED,
        EnrollmentStatus.ELECTION_ACTIVE_STARTED,
        EnrollmentStatus.DEADLINE_PASSED,
      ].includes(enrollmentStatus)
    );

  const { createQleBenefitDocuments } = useCreateQleBenefitDocuments();
  const { removeQleBenefitDocuments } = useRemoveQleBenefitDocuments();

  const submitEditEnrollment = async (data: ChangeEnrollmentsFormFields) => {
    const {
      planYear: enrollmentPeriodId,
      enrollmentType,
      isQleEnrollment,
      qle,
      electionWindow,
      expectedStartOfCoverage,
    } = data;
    const [electionFrom, electionUntil] = electionWindow || [];

    if (!electionFrom || !electionUntil || !expectedStartOfCoverage) {
      showErrorNotification({ message: UNEXPECTED_ERROR_MESSAGE });
      return;
    }

    const enrollmentData = {
      enrollmentPeriodId,
      isSpecialEnrollment: enrollmentType === OnboardingType.SPECIAL,
      onboardingFrom: formatDateISO(electionFrom),
      onboardingUntil: formatDateISO(electionUntil),
      ...(!hasLockedEnrollmentFields
        ? { expectedStartOfCoverage: formatDateISO(expectedStartOfCoverage) }
        : {}),
    };

    let opSucceeded = false;
    const isQlePayload =
      singleEmployeeOnboardingPeriod &&
      singleEmployeeDto &&
      isQleEnrollment &&
      qle?.type &&
      qle?.occurredOn;
    const shouldDeleteQle =
      singleEmployeeOnboardingPeriod &&
      previousIsQleEnrollment &&
      !isQleEnrollment;

    startLoading();

    if (isQlePayload) {
      opSucceeded = await handleQleUpdate(enrollmentData, qle);
    } else if (shouldDeleteQle) {
      opSucceeded = await handleQleDelete(enrollmentData);
    } else {
      opSucceeded = await handleBulkUpdate(enrollmentData);
    }

    if (previousQleDocuments && opSucceeded) {
      await removeQleBenefitDocuments(previousQleDocuments, qle?.evidence);
    }

    await queryClient.invalidateQueries();
    stopLoading();
  };

  const handleQleUpdate = async (
    enrollmentData: Partial<UpdateOnboardingPeriodByAdminDto>,
    qle: QleFormFields
  ) => {
    const updateResult = await updateOnboardingPeriod([
      singleEmployeeOnboardingPeriod!.id,
      idempotencyKey.get(),
      {
        qualifyingLifeEvent: {
          type: qle.type!,
          occurredOn: formatDateISO(qle.occurredOn!),
          otherReason: qle.otherReason,
        },
        ...enrollmentData,
      },
    ]);

    const succeeded = updateResult.isOk();

    if (succeeded) {
      if (qle.evidence) {
        await createQleBenefitDocuments(
          singleEmployeeOnboardingPeriod!.employeeId,
          employerId,
          singleEmployeeOnboardingPeriod!.id,
          qle.evidence
        );
      }
      onSuccess();
    }

    return succeeded;
  };

  const handleQleDelete = async (
    enrollmentData: Partial<UpdateOnboardingPeriodByAdminDto>
  ) => {
    const updateResult = await updateOnboardingPeriod([
      singleEmployeeOnboardingPeriod!.id,
      idempotencyKey.get(),
      {
        qualifyingLifeEvent: null,
        ...enrollmentData,
      },
    ]);

    const succeeded = updateResult.isOk();

    if (succeeded) {
      onSuccess();
    }

    return succeeded;
  };

  const handleBulkUpdate = async (
    enrollmentData: Partial<UpdateOnboardingPeriodByAdminDto>
  ): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      updateEnrollments(
        recordsForActions
          .filter((record) => record.onboardingPeriodId !== undefined)
          .map(({ fullName, onboardingPeriodId }) => ({
            key: fullName,
            params: [
              onboardingPeriodId!,
              idempotencyKey.getFor(onboardingPeriodId!),
              enrollmentData,
            ],
          })),
        {
          onSuccess: () => {
            onSuccess();
            resolve(true);
          },
        }
      ).catch(reject);
    });
  };

  return (
    <>
      <ChangeEnrollmentForm
        monthlySEPDeadline={employer?.monthlySEPDeadline ?? null}
        isMonthlySEPDeadlineEndOfMonth={
          employer?.isMonthlySEPDeadlineEndOfMonth ?? null
        }
        form={form}
        onSubmit={submitEditEnrollment}
        allOpenEnrollmentPeriods={allOpenEnrollmentPeriods}
        singleEmployeeDto={singleEmployeeDto}
        associatedOtherOnboardingPeriodsSingleEmployee={
          associatedOtherOnboardingPeriodsSingleEmployee
        }
        enrollmentTeams={employerAgencies?.enrollmentTeams || []}
        shouldHideEnrollmentTeamFields
        hasLockedEnrollmentFields={hasLockedEnrollmentFields}
      />

      <ErrorsTable
        tableTitle="Employee name"
        errors={updateEnrollmentsErrors}
        isBulk={recordsForActions.length > 1}
      />
    </>
  );
};
